import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  TuiAvatar,
  TuiAutoColorPipe,
  TuiInitialsPipe,
  TuiSkeleton,
} from '@taiga-ui/kit';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  DeviceDetectorService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { WorkflowLogoutGuardService } from '@core/services/workflow/workflow-logout-guard.service';
import { BehaviorSubject, take } from 'rxjs';
import { environment } from '@environment/environment';
import {
  TuiError,
  TuiExpand,
  TuiButton,
  TuiInput,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { catchError } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  Avatar = 'avatar',
  Email = 'email',
  Fullname = 'fullname',
  NewPassword = 'newPassword',
  Confirmation = 'confirmation',
}

const passwordMatchValidator: ValidatorFn = (
  group: AbstractControl,
): ValidationErrors | null => {
  const password = group.get(FormFields.NewPassword)?.value;
  const confirmation = group.get(FormFields.Confirmation)?.value;

  return password === confirmation ? null : { passwordMismatch: true };
};

@Component({
  selector: 'x-profile-page',
  imports: [
    TuiSkeleton,
    TuiTextfield,
    TuiAutoColorPipe,
    TuiInitialsPipe,
    NgClass,
    AsyncPipe,
    TuiAvatar,
    TuiButton,
    ReactiveFormsModule,
    TuiInput,
    TuiError,
    TuiExpand,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  templateUrl: './profile-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePageComponent {
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly accountService = inject(AccountService);
  private readonly workflowLogoutGuard = inject(WorkflowLogoutGuardService);
  readonly deviceService = inject(DeviceDetectorService);
  readonly currentUser$ = this.accountService.currentUser$;
  readonly $currentUser = this.accountService.$currentUser;
  private xAlertService = inject(XAlertService);
  private readonly formBuilder = inject(FormBuilder);
  public $changePasswordBlockVisible = signal(false);

  constructor() {
    effect(() => {
      if (this.$currentUser()) {
        const user = this.$currentUser()!;

        this.userForm.patchValue({
          [FormFields.Avatar]: this.getAvatarUrl(user.avatar),
          [FormFields.Email]: user.email,
          [FormFields.Fullname]: user.fullname,
        });
      }
    });
  }

  public FormFields = FormFields;

  public userForm = this.formBuilder.group({
    [FormFields.Avatar]: [''],
    [FormFields.Email]: [
      '',
      {
        validators: [Validators.required, Validators.email],
        nonNullable: true,
      },
    ],
    [FormFields.Fullname]: ['', Validators.required],
  });

  public changePasswordForm = this.formBuilder.group(
    {
      [FormFields.NewPassword]: [
        '',
        [Validators.required, Validators.minLength(4)],
      ],
      [FormFields.Confirmation]: ['', [Validators.required]],
    },
    { validators: passwordMatchValidator },
  );

  public getAvatarUrl(avatar?: string) {
    if (avatar) {
      return environment.staticUrl + avatar;
    }

    return '';
  }

  public async logout() {
    await this.workflowLogoutGuard.logout();
  }

  public updateProfile() {
    if (!this.emailField.valid) {
      return;
    }

    this.accountService
      .updateCurrentUser$({
        email: this.emailField.value!,
        fullname: this.fullnameField.value!,
        avatar: this.avatarField.value!,
      })
      .pipe(
        catchError(err => {
          this.xAlertService.error(err?.error || 'Error');

          throw err;
        }),
        take(1),
      )
      .subscribe(() => {
        this.xAlertService.success('OK');
      });
  }

  public setNewPassword() {
    if (this.changePasswordForm.invalid) {
      return;
    }

    this.accountService
      .updateCurrentUser$({
        password: this.newPasswordField.value!,
        confirmation: this.confirmationField.value!,
      })
      .pipe(
        catchError(err => {
          this.xAlertService.error(err?.error || 'Error');

          throw err;
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          this.changePasswordForm.reset();
          this.$changePasswordBlockVisible.set(false);
          this.xAlertService.success('OK');
        },
      });
  }

  public toggleShowSetNewPasswordBlock() {
    this.$changePasswordBlockVisible.update(v => !v);
  }

  get avatarField() {
    return this.userForm.controls[FormFields.Avatar];
  }
  get emailField() {
    return this.userForm.controls[FormFields.Email];
  }
  get fullnameField() {
    return this.userForm.controls[FormFields.Fullname];
  }
  get newPasswordField() {
    return this.changePasswordForm.controls[FormFields.NewPassword];
  }
  get confirmationField() {
    return this.changePasswordForm.controls[FormFields.Confirmation];
  }

  public resetUserSettings() {
    this.accountService
      .resetUserSettings$(['common', 'outkeep'])
      .pipe(
        take(1),
        catchError(err => {
          this.xAlertService.error(
            err?.error?.data || err?.error?.message || 'Error',
          );

          throw err;
        }),
      )
      .subscribe(() => {
        this.xAlertService.success('OK');
      });
  }
}
